/**
 * Shopping cart — **client-side only**. The tenant API has no member cart
 * endpoint (confirmed against tenant-api routes + the Angular member-portal,
 * which also keeps the cart in localStorage). The cart is materialised into
 * a real order at checkout via `POST /public/orders`.
 *
 * Persisted under a per-namespace key so it survives reloads. Functions
 * stay async so screens that `await` them keep working unchanged.
 */

const STORAGE_KEY = 'paas_cart';

export interface CartItem {
  cart_item_id: string;
  product_id: string;
  variant_id?: string;
  name: string;
  thumbnail_url?: string;
  quantity: number;
  unit_price: number;
  currency: string;
  line_total: number;
}

export interface Cart {
  cart_id: string;
  items: CartItem[];
  subtotal: number;
  shipping?: number;
  tax?: number;
  total: number;
  currency: string;
}

function emptyCart(): Cart {
  return { cart_id: 'local', items: [], subtotal: 0, total: 0, currency: 'ZAR' };
}

function readCart(): Cart {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyCart();
    return recompute(JSON.parse(raw) as Cart);
  } catch {
    return emptyCart();
  }
}

function writeCart(cart: Cart): Cart {
  const next = recompute(cart);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage full / disabled — cart stays in memory for this session */
  }
  return next;
}

function recompute(cart: Cart): Cart {
  const items = cart.items.map((i) => ({ ...i, line_total: i.unit_price * i.quantity }));
  const subtotal = items.reduce((sum, i) => sum + i.line_total, 0);
  return {
    ...cart,
    items,
    subtotal,
    total: subtotal + (cart.shipping ?? 0) + (cart.tax ?? 0),
    currency: items[0]?.currency ?? cart.currency ?? 'ZAR',
  };
}

export async function fetchCart(): Promise<Cart> {
  return readCart();
}

export async function addCartItem(payload: {
  product_id: string;
  variant_id?: string;
  quantity: number;
  name?: string;
  unit_price?: number;
  currency?: string;
  thumbnail_url?: string;
}): Promise<Cart> {
  const cart = readCart();
  const existing = cart.items.find(
    (i) => i.product_id === payload.product_id && i.variant_id === payload.variant_id,
  );
  if (existing) {
    existing.quantity += payload.quantity;
  } else {
    cart.items.push({
      cart_item_id: `${payload.product_id}:${payload.variant_id ?? ''}:${Date.now()}`,
      product_id: payload.product_id,
      variant_id: payload.variant_id,
      name: payload.name ?? 'Item',
      thumbnail_url: payload.thumbnail_url,
      quantity: payload.quantity,
      unit_price: payload.unit_price ?? 0,
      currency: payload.currency ?? 'ZAR',
      line_total: (payload.unit_price ?? 0) * payload.quantity,
    });
  }
  return writeCart(cart);
}

export async function updateCartItem(cartItemId: string, quantity: number): Promise<Cart> {
  const cart = readCart();
  const item = cart.items.find((i) => i.cart_item_id === cartItemId);
  if (item) item.quantity = Math.max(1, quantity);
  return writeCart(cart);
}

export async function removeCartItem(cartItemId: string): Promise<Cart> {
  const cart = readCart();
  cart.items = cart.items.filter((i) => i.cart_item_id !== cartItemId);
  return writeCart(cart);
}

export async function clearCart(): Promise<Cart> {
  return writeCart(emptyCart());
}
